import {
  AssistantsService,
  KnowledgeBasesV2Service,
  WorkflowService,
} from "@/generated/sdk.gen";
import { KbType } from "@/generated";
import { SpotToast } from "@/components/SpotToast";
import { ANALYTICS_EVENTS, analytics_track } from "@/utils/mixpanel";
import { DATA_KEYS } from "@/utils/useRemoteData";
import { useSpotUser } from "@/utils/spotUser";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import { useSetAtom } from "jotai";
import { isCreateTableOpenAtom } from "@/store/ui-atoms";
import {
  getEmptyFlowDefinition,
  isEditorV2Enabled,
} from "@/utils/emptyFlowDefinition";
const onCreateError = (fallback: string) => (_error: unknown) => {
  SpotToast.error(fallback);
};

export type CreateNewToolId =
  | "agentic-workflow"
  | "agentic-workflow-v2"
  | "co-worker"
  | "knowledge-base"
  | "table-tool";

export function useCreateNewTool({
  onCreated,
  onError,
  onNavigate,
}: {
  onCreated?: () => void;
  onError?: () => void;
  // Right before the new tool's page is opened.
  onNavigate?: () => void;
} = {}) {
  const router = useRouter();
  const { spotGroup } = useSpotUser();
  const queryClient = useQueryClient();
  const setCreateTableOpen = useSetAtom(isCreateTableOpenAtom);

  const create_flow_mutation = useMutation({
    mutationFn: WorkflowService.addWorkflow,
    onSuccess: (data) => {
      analytics_track(ANALYTICS_EVENTS.WORKFLOW_CREATE, {
        workflow_id: data.data.id,
      });
      queryClient.invalidateQueries({ queryKey: [DATA_KEYS.WORKFLOWS] });
      onNavigate?.();
      router.push(`/flows/${data.data.id}`);
      onCreated?.();
    },
    onError: (error) => {
      onCreateError(
        "There was an error creating your Flow. Please try again later."
      )(error);
      onError?.();
    },
  });

  const create_kb_mutation = useMutation({
    mutationFn: KnowledgeBasesV2Service.addKnowledgeBaseV2,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [DATA_KEYS.KNOWLEDGE_BASES_V2],
      });
      analytics_track(ANALYTICS_EVENTS.KNOWLEDGE_BASE_CREATE, {
        knowledge_base_id: data.data.id,
        kb_type: data.data.kb_type,
      });
      onNavigate?.();
      router.push(`/data-hub/${data.data.id}`);
      onCreated?.();
    },
    onError: (error) => {
      onCreateError("Failed to create Knowledge Base")(error);
      onError?.();
    },
  });

  const create_assistant_mutation = useMutation({
    mutationFn: AssistantsService.addAssistant,
    onSuccess: (data) => {
      analytics_track(ANALYTICS_EVENTS.COWORKER_CREATE, {
        assistant_id: data.data.id,
      });
      onNavigate?.();
      router.push(`/assistant/${data.data.id}`);
      onCreated?.();
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: [DATA_KEYS.ASSISTANTS] });
      }, 500);
    },
    onError: (error) => {
      onCreateError("Failed to create Agent")(error);
      onError?.();
    },
  });

  const createNewTool = (requestedToolId: CreateNewToolId) => {
    // In Editor V2 workspaces every "Create Flow" entry point should create a
    // V2 flow — upgrade the plain workflow id so callers don't each have to
    // branch on the setting.
    const editorV2Enabled = isEditorV2Enabled(spotGroup.settings);
    const toolId: CreateNewToolId =
      requestedToolId === "agentic-workflow" && editorV2Enabled
        ? "agentic-workflow-v2"
        : requestedToolId;

    // Tables are the one tool that cannot be created blank: the display name
    // must be unique in the workspace and the row-id type is fixed for the
    // table's lifetime. The dialog collecting both is mounted in the dashboard
    // layout, so opening it is all this has to do.
    if (toolId === "table-tool") {
      setCreateTableOpen(true);
      onCreated?.();
      return;
    }

    if (toolId === "agentic-workflow" || toolId === "agentic-workflow-v2") {
      create_flow_mutation.mutate({
        path: { group_id: spotGroup.id },
        body: {
          type: "flow",
          name: "Untitled flow",
          definition: getEmptyFlowDefinition(toolId === "agentic-workflow-v2"),
        },
      });
      return;
    }

    if (toolId === "co-worker") {
      create_assistant_mutation.mutate({
        path: { group_id: spotGroup.id },
        body: { name: "Untitled agent" },
      });
      return;
    }

    if (toolId === "knowledge-base") {
      create_kb_mutation.mutate({
        path: { group_id: spotGroup.id },
        body: {
          name: "Untitled knowledge base",
          description: "",
          kb_type: "entity" as KbType,
          document_types: ["document"],
        },
      });
    }
  };

  const isCreating =
    create_flow_mutation.status === "pending" ||
    create_assistant_mutation.status === "pending" ||
    create_kb_mutation.status === "pending";

  return { createNewTool, isLoading: isCreating };
}
